import { useState } from 'react'
import { Button, CircularProgress } from '@mui/material'
import GoogleIcon from '@mui/icons-material/Google'
import { useGoogleLogin } from '@react-oauth/google'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import ApiService from '../services/ApiService'
import ProjectService from '../services/ProjectService'
import { connectSocket, socketEventEmitter, subscribeToCopyJob } from '../socket'

const COPY_JOB_TERMINAL_STATUSES = ['done', 'failed']
const COPY_JOB_WAIT_TIMEOUT_MS = 130000
const COPY_JOB_POLL_INTERVAL_MS = 3000

const isTerminalCopyJob = (copyJob) =>
  !copyJob || COPY_JOB_TERMINAL_STATUSES.includes(copyJob.status)

const waitForCopyJob = (copyJob, userId) => {
  if (isTerminalCopyJob(copyJob)) return Promise.resolve()

  return new Promise((resolve) => {
    let resolved = false
    let timeoutId
    let pollId

    const cleanup = () => {
      socketEventEmitter.off('copyJobUpdate', handleJobUpdate)
      clearTimeout(timeoutId)
      clearInterval(pollId)
    }

    const resolveOnce = () => {
      if (resolved) return
      resolved = true
      cleanup()
      resolve()
    }

    const handleJobUpdate = (update) => {
      if (update?.jobId !== copyJob.id) return
      if (COPY_JOB_TERMINAL_STATUSES.includes(update.status)) {
        resolveOnce()
      }
    }

    const pollProjects = async () => {
      try {
        const res = await ProjectService.getAllProjects(1, 1000)
        if (res?.projects?.length) {
          resolveOnce()
        }
      } catch {
        // Keep waiting for the socket event or timeout.
      }
    }

    const serverUrl = process.env.REACT_APP_SERVER_URL
    connectSocket(serverUrl, userId, { copyJobId: copyJob.id })
    subscribeToCopyJob(copyJob.id)
    socketEventEmitter.on('copyJobUpdate', handleJobUpdate)

    timeoutId = setTimeout(resolveOnce, COPY_JOB_WAIT_TIMEOUT_MS)
    pollId = setInterval(pollProjects, COPY_JOB_POLL_INTERVAL_MS)
    pollProjects()
  })
}

const CustomGoogleLoginButton = () => {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [buttonState, setButtonState] = useState('idle')

  const handleGoogleLoginSuccess = async (tokenResponse) => {
    const accessToken = tokenResponse.access_token
    try {
      setButtonState('signingIn')
      const data = await ApiService.post('/auth/google-login', { google_access_token: accessToken })
      const { access_token, refresh_token, user, copyJob } = data
      await login(access_token, refresh_token, user.id)

      if (!isTerminalCopyJob(copyJob)) {
        setButtonState('settingUp')
        await waitForCopyJob(copyJob, user.id)
      }

      navigate('/')
    } catch (error) {
      console.log('Error during Google login:', error)
      setButtonState('idle')
    }
  }

  const handleGoogleLoginError = () => {
    console.log('Login Failed')
    setButtonState('idle')
  }

  const handleGoogleNonOAuthError = () => {
    setButtonState('idle')
  }

  const loginWithGoogle = useGoogleLogin({
    onSuccess: handleGoogleLoginSuccess,
    onError: handleGoogleLoginError,
    onNonOAuthError: handleGoogleNonOAuthError,
  })

  const handleButtonClick = () => {
    setButtonState('signingIn')
    loginWithGoogle()
  }

  const isLoading = buttonState !== 'idle'
  const buttonLabel = buttonState === 'settingUp' ? 'Setting up account...' : 'Sign in with Google'

  return (
    <Button
      variant="outlined"
      color="secondary"
      sx={{ width: '250px', textTransform: 'none' }}
      disabled={isLoading}
      onClick={handleButtonClick}
    >
      {isLoading ? (
        <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
      ) : (
        <GoogleIcon sx={{ height: '16px', marginBottom: '2px', mr: 1 }} />
      )}
      {buttonLabel}
    </Button>
  )
}

export default CustomGoogleLoginButton
